import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useToast } from './ToastContext';
import { validatePtsDetailCalculations } from '../Utils/validatePtsDetailCalculations';

const ProcessedTimesheetContext = createContext();

export const useProcessedTimesheetContext = () => {
    const context = useContext(ProcessedTimesheetContext);
    if (!context) {
        throw new Error(
            'useProcessedTimesheetContext must be used within a ProcessedTimesheetContextProvider'
        );
    }
    return context;
};

export const ProcessedTimesheetContextProvider = ({ children }) => {
    const [hasRecentChanges, setHasRecentChanges] = useState(false);
    const [calculationErrorEntryIds, setCalculationErrorEntryIds] = useState([]);
    const { showErrorToast } = useToast();

    const handleOutdatedError = useCallback(
        (apiError, errorCb) => {
            if (apiError?.errorCode === 422) {
                setHasRecentChanges(true);
                showErrorToast(
                    'This transaction has been updated, and your changes cannot be saved. Please refresh the page to see the latest values. Any unsaved changes will be lost after refreshing.'
                );
            } else {
                errorCb?.(apiError);
            }
        },
        [showErrorToast]
    );

    const clearCalculationErrorEntryIds = useCallback(() => {
        setCalculationErrorEntryIds([]);
    }, []);

    const syncCalculationErrorEntryIds = useCallback((entries = [], locations = []) => {
        setCalculationErrorEntryIds((currentEntryIds) => {
            if (!currentEntryIds.length) {
                return currentEntryIds;
            }

            const remainingEntryIds = currentEntryIds.filter((entryId) => {
                const entry = entries.find(({ id }) => id === entryId);
                if (!entry) {
                    return false;
                }

                return validatePtsDetailCalculations([entry], locations).length > 0;
            });

            return remainingEntryIds;
        });
    }, []);

    const contextValue = useMemo(
        () => ({
            hasRecentChanges,
            setHasRecentChanges,
            handleOutdatedError,
            calculationErrorEntryIds,
            setCalculationErrorEntryIds,
            clearCalculationErrorEntryIds,
            syncCalculationErrorEntryIds,
        }),
        [
            hasRecentChanges,
            handleOutdatedError,
            calculationErrorEntryIds,
            clearCalculationErrorEntryIds,
            syncCalculationErrorEntryIds,
        ]
    );

    return (
        <ProcessedTimesheetContext.Provider value={contextValue}>
            {children}
        </ProcessedTimesheetContext.Provider>
    );
};
